import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTicketStats } from '../services/api';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  History, 
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronDown,
  LifeBuoy,
  Database,
  User,
  Lock
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import NotificationBell from './NotificationBell';
import ProfileDialog from './ProfileDialog';

const Layout = ({ children }) => {
  const { user, logout, isAdmin, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openTickets, setOpenTickets] = useState(0);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileInitialTab, setProfileInitialTab] = useState('profile');

  useEffect(() => {
    let mounted = true;
    const fetchOpenTickets = async () => {
      if (!isAdmin() || !hasPermission('tickets', 'view')) return;
      try {
        const res = await getTicketStats();
        if (mounted) setOpenTickets(res.data.aberto || 0);
      } catch (e) {
        // silent
      }
    };
    fetchOpenTickets();
    const interval = setInterval(fetchOpenTickets, 30000);
    return () => { mounted = false; clearInterval(interval); };
  }, [user]);

  const navItems = [];
  if (hasPermission('dashboard', 'view')) {
    navItems.push({ path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' });
  }
  if (hasPermission('licenses', 'view')) {
    navItems.push({ path: '/licenses', icon: FileText, label: 'Licenças e Alvarás' });
  }
  if (hasPermission('tickets', 'view')) {
    navItems.push({ path: '/tickets', icon: LifeBuoy, label: 'Chamados', badge: isAdmin() ? openTickets : 0 });
  }
  if (hasPermission('history', 'view')) {
    navItems.push({ path: '/history', icon: History, label: 'Histórico' });
  }
  if (hasPermission('users', 'manage')) {
    navItems.push({ path: '/users', icon: Users, label: 'Usuários' });
  }
  if (hasPermission('settings', 'manage')) {
    navItems.push({ path: '/settings', icon: Settings, label: 'Configurações' });
    navItems.push({ path: '/backups', icon: Database, label: 'Backups' });
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  const getRoleName = (role) => {
    const roles = {
      admin: 'Administrador',
      gestor: 'Gestor',
      usuario: 'Usuário'
    };
    return roles[role] || role;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
              <img 
                src={`${process.env.PUBLIC_URL}/logo-kn.png`} 
                alt="Kuehne+Nagel" 
                className="h-8 w-auto object-contain"
              />
            </Link>
            <button 
              className="lg:hidden text-gray-500 hover:text-gray-700"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || 
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.slice(1)}`}
                  className={`flex items-center gap-3 mx-2 px-4 py-2.5 rounded-md transition-colors font-semibold ${
                    isActive 
                      ? 'bg-blue-50 text-[#003366] border-r-2 border-[#003366]' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-5 h-5" />
                  <span className="flex-1">{item.label}</span>
                  {item.badge > 0 && (
                    <span
                      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full bg-red-500 text-white"
                      data-testid={`nav-badge-${item.path.slice(1)}`}
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 p-3 rounded-md bg-gray-50">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-[#003366] text-white text-sm font-semibold">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500">{getRoleName(user?.role)}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white/90 backdrop-blur-md border-b border-gray-200">
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            <button 
              className="lg:hidden text-gray-500 hover:text-gray-700"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1" />

            <div className="flex items-center gap-3">
              <NotificationBell />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                    data-testid="user-menu-btn"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-[#003366] text-white text-xs font-semibold">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-56 bg-white border-gray-200"
                >
                  <div className="px-3 py-2">
                    <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-gray-200" />
                  <DropdownMenuItem
                    className="text-gray-700 focus:bg-gray-100 focus:text-gray-900 cursor-pointer"
                    onClick={() => { setProfileInitialTab('profile'); setProfileDialogOpen(true); }}
                    data-testid="profile-menu-item"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Meu Perfil
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-gray-700 focus:bg-gray-100 focus:text-gray-900 cursor-pointer"
                    onClick={() => { setProfileInitialTab('password'); setProfileDialogOpen(true); }}
                    data-testid="change-password-menu-item"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Alterar Senha
                  </DropdownMenuItem>
                  {hasPermission('settings', 'manage') && (
                    <>
                      <DropdownMenuSeparator className="bg-gray-200" />
                      <DropdownMenuItem 
                        className="text-gray-700 focus:bg-gray-100 focus:text-gray-900 cursor-pointer"
                        onClick={() => navigate('/settings')}
                        data-testid="settings-menu-item"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Configurações
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-gray-200" />
                  <DropdownMenuItem 
                    className="text-rose-600 focus:bg-rose-50 focus:text-rose-700 cursor-pointer"
                    onClick={handleLogout}
                    data-testid="logout-menu-item"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>

      <ProfileDialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen} initialTab={profileInitialTab} />
    </div>
  );
};

export default Layout;
